import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { physicalMeetingTransformer } from './CreatePhysicalMeeting.transformers';
import {
  CreatePhysicalMeetingStepsIndex,
  CreatePhysicalMeetingWithEmailConfigurationStepsIndex,
} from './CreatePhysicalMeeting.types';
import useCreatePhysicalMeetingButtons from './CreatePhysicalMeetingButtons.hook';
import useCreatePhysicalMeetingStepper from './CreatePhysicalMeetingStepper.hook';
import { eventsRoute } from 'basics/constants/routes.constants';
import { warningToast } from 'basics/utils/toast';
import { EventStatus, Presence } from 'generated/event.types';
import { isDraft } from 'modules/events/basics/utils/events.utils';
import { EditEventButtonActionStates } from 'modules/events/components/EditEventButtonsBar/EditEventButtonsBar.types';
import useLazyGetFullPrivateEventById from 'modules/events/graphql/queries/getLazyFullPrivateEventById';
import useCreatePhysicalMeetingActions from 'modules/events/routes/CreatePhysicalMeeting/CreatePhysicalMeeting.actions.hook';
import eventState from 'modules/events/states/events.states';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCreatePhysicalMeeting = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const flags = useFlags();
  const yodaFormMethods = useYodaCreateForm({ setIsValidOptional: true });
  const PhysicalMeetingActions = useCreatePhysicalMeetingActions(yodaFormMethods);
  const { queryGetFullPrivateEventById, data: event, error, loading } = useLazyGetFullPrivateEventById();

  if (event && event.presence !== Presence.physical) {
    warningToast(t('errors_standard'));
    navigate(eventsRoute);
  }

  const buttonsConfig = useCreatePhysicalMeetingButtons(
    PhysicalMeetingActions,
    !isDraft(event?.status),
  );
  const stepsList = useCreatePhysicalMeetingStepper(yodaFormMethods.useWatchStep);

  const isLastStep = flags.eventsBiogenlinc4151SectionEmail
    ? PhysicalMeetingActions.stepIndexer.stepIndex === CreatePhysicalMeetingWithEmailConfigurationStepsIndex.publicationStep
    : PhysicalMeetingActions.stepIndexer.stepIndex === CreatePhysicalMeetingStepsIndex.publicationStep;

  const actionStates: EditEventButtonActionStates = {
    updateLoading: PhysicalMeetingActions.updateLoading,
    publishLoading: PhysicalMeetingActions.publishLoading,
    readyToPublish: yodaFormMethods.formState.isValid && event?.status === EventStatus.ready,
    isLastStep,
  };

  useEffect(() => {
    if (id) {
      queryGetFullPrivateEventById({ variables: { eventId: id } });
    }
  }, [id, queryGetFullPrivateEventById]);

  useEffect(() => {
    if (event) {
      eventState.createEvent = physicalMeetingTransformer(event);
    }
    if (error) {
      warningToast(t('errors_standard'));
      navigate(eventsRoute);
    }
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickHome = async () => {
    navigate(eventsRoute);
  };

  const showLoader = loading || !event;

  return {
    t,
    handleClickHome,
    handleChangeStep: PhysicalMeetingActions.handleChangeStep,
    showLoader,
    yodaFormMethods,
    stepIndexer: PhysicalMeetingActions.stepIndexer,
    id,
    buttonsConfig,
    actionStates,
    stepsList,
    event,
  };
};

export default useCreatePhysicalMeeting;
